import type { FC } from "react"
import { useState } from "react"

import { Group } from "@visx/group"
import { ParentSize } from "@visx/responsive"
import { scaleBand } from "@visx/scale"

import type { UsagePatternsViewDto } from "~/api/dto/proposal-consumption.dto"

import {
  HEATMAP_GAP_FILL,
  HEATMAP_IMPUTED_TICK,
  LABEL_COLOR,
  formatHour,
  formatKwh,
  heatmapRampColor,
} from "./chart-theme"

const MARGIN = { top: 10, right: 8, bottom: 28, left: 40 }

type HoverCell = { dayIndex: number; hour: number }

type ChartProps = {
  height: number
  viewModel: UsagePatternsViewDto
  width: number
}

const Chart: FC<ChartProps> = ({ height, viewModel, width }) => {
  const [hover, setHover] = useState<HoverCell | null>(null)
  const innerWidth = width - MARGIN.left - MARGIN.right
  const innerHeight = height - MARGIN.top - MARGIN.bottom
  const dayCount = viewModel.matrix.length
  if (innerWidth <= 0 || innerHeight <= 0 || dayCount === 0) return null

  const dayScale = scaleBand<number>({
    domain: Array.from({ length: dayCount }, (_, i) => i),
    range: [0, innerWidth],
  })
  const hourScale = scaleBand<number>({
    domain: Array.from({ length: 24 }, (_, i) => i),
    range: [0, innerHeight],
  })
  const cellW = dayScale.bandwidth()
  const cellH = hourScale.bandwidth()
  const maxKwh = Math.max(viewModel.maxKwh, 0.0001)
  const hoverValue = hover
    ? viewModel.matrix[hover.dayIndex]?.[hover.hour]
    : null

  return (
    <div className="relative">
      <svg
        height={height}
        role="img"
        aria-label={`Hourly ${viewModel.measurementBasis === `GRID_IMPORT` ? `grid import` : `usage`} intensity across the year`}
        width={width}
      >
        <Group left={MARGIN.left} top={MARGIN.top}>
          {viewModel.matrix.map((hours, dayIndex) =>
            hours.map((kwh, hour) => (
              <rect
                key={`${dayIndex}-${hour}`}
                fill={
                  kwh === null
                    ? HEATMAP_GAP_FILL
                    : heatmapRampColor(Math.min(1, kwh / maxKwh))
                }
                height={cellH}
                width={cellW}
                x={dayScale(dayIndex)}
                y={hourScale(hour)}
              />
            )),
          )}
          {viewModel.imputedDayIndexes.map((dayIndex) => (
            <rect
              key={`imputed-${dayIndex}`}
              fill={HEATMAP_IMPUTED_TICK}
              height={3}
              width={cellW}
              x={dayScale(dayIndex)}
              y={-6}
            />
          ))}
          <rect
            fill="transparent"
            height={innerHeight}
            width={innerWidth}
            x={0}
            y={0}
            onMouseLeave={() => setHover(null)}
            onMouseMove={(event) => {
              const rect = event.currentTarget.getBoundingClientRect()
              const x = event.clientX - rect.left
              const y = event.clientY - rect.top
              const dayIndex = Math.max(
                0,
                Math.min(dayCount - 1, Math.floor((x / innerWidth) * dayCount)),
              )
              const hour = Math.max(
                0,
                Math.min(23, Math.floor((y / innerHeight) * 24)),
              )
              setHover({ dayIndex, hour })
            }}
          />
          {[0, 6, 12, 18, 23].map((hour) => (
            <text
              key={hour}
              fill={LABEL_COLOR}
              fontFamily="Barlow, sans-serif"
              fontSize={10}
              textAnchor="end"
              x={-6}
              y={(hourScale(hour) ?? 0) + cellH / 2 + 3}
            >
              {formatHour(hour)}
            </text>
          ))}
          {viewModel.monthTicks.map((tick) => (
            <text
              key={tick.dayIndex}
              fill={LABEL_COLOR}
              fontFamily="Barlow, sans-serif"
              fontSize={10}
              x={dayScale(tick.dayIndex)}
              y={innerHeight + 16}
            >
              {tick.label}
            </text>
          ))}
        </Group>
      </svg>
      {hover ? (
        <div className="pointer-events-none absolute top-2 right-2 rounded-lg border border-border bg-background px-3 py-2 shadow-md">
          <p className="font-display text-xs font-bold text-foreground">
            Day {hover.dayIndex + 1} · {formatHour(hover.hour)}
          </p>
          <p className="mt-0.5 text-xs text-muted">
            {hoverValue === null || hoverValue === undefined ? (
              `No data`
            ) : (
              <span className="font-semibold tabular-nums text-foreground">
                {formatKwh(hoverValue)}
              </span>
            )}
          </p>
        </div>
      ) : null}
    </div>
  )
}

export const HourDayHeatmap: FC<{ viewModel: UsagePatternsViewDto }> = ({
  viewModel,
}) => (
  <div>
    <div className="flex flex-wrap items-center gap-4 text-xs font-semibold text-muted">
      <span className="flex items-center gap-1.5">
        <span
          className="inline-block h-2.5 w-10 rounded-sm"
          style={{
            background: `linear-gradient(to right, ${heatmapRampColor(0)}, ${heatmapRampColor(1)})`,
          }}
        />
        Low to high kWh
      </span>
      <span className="flex items-center gap-1.5">
        <span
          className="inline-block size-2.5 rounded-sm"
          style={{ backgroundColor: HEATMAP_GAP_FILL }}
        />
        No data
      </span>
      <span className="flex items-center gap-1.5">
        <span
          className="inline-block h-1 w-4 rounded-full"
          style={{ backgroundColor: HEATMAP_IMPUTED_TICK }}
        />
        Imputed days
      </span>
    </div>
    <div className="mt-3 h-[320px]">
      <ParentSize>
        {({ height, width }) => (
          <Chart height={height} viewModel={viewModel} width={width} />
        )}
      </ParentSize>
    </div>
  </div>
)
